import {
  UNKNOWN_PARTICIPANT_DISPLAY_NAME,
  UserIdentity,
} from "../schemas/user-identity.schemas.js";
import type { UserIdentityInput } from "../schemas/user-identity.schemas.js";

// Only the `value` of each name item is read, so accept any shape providing it
// — both the full `userDataItem` ({value,source,updatedAt}) and the trimmed
// view `{ value }` projection satisfy this.
interface NameValue {
  readonly value: string | null;
}

export interface ParticipantNameSource {
  readonly firstName: NameValue;
  readonly lastName: NameValue;
  readonly fullName: NameValue;
  readonly nickName: NameValue;
}

function toUserIdentityInput(
  participant: ParticipantNameSource,
): UserIdentityInput {
  return {
    firstName: participant.firstName.value,
    lastName: participant.lastName.value,
    fullName: participant.fullName.value,
    nickName: participant.nickName.value,
  };
}

function toUserIdentity(participant: ParticipantNameSource): UserIdentity {
  return UserIdentity.unsafeCreate(toUserIdentityInput(participant));
}

export function getParticipantDisplayName(
  participant: ParticipantNameSource,
): string {
  return UserIdentity.getDisplayName(toUserIdentity(participant));
}

export function getParticipantFullName(
  participant: ParticipantNameSource,
): string | null {
  return UserIdentity.getFullName(toUserIdentity(participant));
}

export function getParticipantGreetingName(
  participant: ParticipantNameSource,
): string | undefined {
  return UserIdentity.getGreetingName(toUserIdentity(participant));
}

export function compareParticipantsByName(
  a: ParticipantNameSource,
  b: ParticipantNameSource,
): number {
  return UserIdentity.compareByName(toUserIdentity(a), toUserIdentity(b));
}

/**
 * Display name of a participant who may have none.
 *
 * {@link getParticipantDisplayName} throws on a nameless participant, which is a
 * document that really exists; every list degrades that row here so no two
 * surfaces name the same person differently. Built through `tryCreate` so a blank
 * part falls through to a real name in a lower-priority one.
 */
export function resolveParticipantDisplayName(
  names: UserIdentityInput,
): string {
  const identity = UserIdentity.tryCreate(names);
  return identity === undefined
    ? UNKNOWN_PARTICIPANT_DISPLAY_NAME
    : UserIdentity.getDisplayName(identity);
}

/**
 * {@link resolveParticipantDisplayName} for a participant whose name fields
 * still carry their provenance wrappers, so entity-shaped callers degrade a
 * nameless row exactly like the projection-shaped ones do.
 */
export function resolveParticipantDisplayNameFromSource(
  participant: ParticipantNameSource,
): string {
  return resolveParticipantDisplayName(toUserIdentityInput(participant));
}
